import {
  type DocumentData,
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { getFirebaseDb } from '../app';
import { getDocument, updateDocument } from '../firestore';

function getProfilesPath(uid: string): string {
  return `users/${uid}/profiles`;
}

export async function getProfile(
  uid: string,
  profileId: string,
): Promise<(DocumentData & { id: string }) | null> {
  return getDocument(getProfilesPath(uid), profileId);
}

export async function getAllProfiles(uid: string): Promise<(DocumentData & { id: string })[]> {
  const db = getFirebaseDb();
  const profilesRef = collection(db, getProfilesPath(uid));
  const snapshot = await getDocs(query(profilesRef));

  // Returns every profile — including inactive AND terminated. Callers that
  // only want active ones must filter client-side or use getActiveProfiles.
  // Why include terminated: the Profiles page and Appointments profile filter
  // need to surface old profiles so historical charts owned by them remain
  // discoverable; defaulting them hidden is a UI concern, not a data one.
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getActiveProfiles(uid: string): Promise<(DocumentData & { id: string })[]> {
  const db = getFirebaseDb();
  const profilesRef = collection(db, getProfilesPath(uid));
  const snapshot = await getDocs(query(profilesRef));

  // Filter client-side: include profiles where inactive is null, false, or missing
  // Exclude only profiles explicitly marked inactive: true or isTerminated: true
  return snapshot.docs
    .filter((d) => {
      const data = d.data();
      return data['isTerminated'] !== true && data['inactive'] !== true;
    })
    .map((d) => ({ id: d.id, ...d.data() }));
}

export async function createProfile(uid: string, data: Record<string, unknown>): Promise<string> {
  const db = getFirebaseDb();
  const profilesRef = collection(db, getProfilesPath(uid));
  const docRef = await addDoc(profilesRef, {
    createdAt: serverTimestamp(),
    inactive: null,
    isTerminated: null,
    email: null,
    lastActiveAt: null,
    ...data,
  });
  return docRef.id;
}

export async function updateProfile(
  uid: string,
  profileId: string,
  data: Record<string, unknown>,
): Promise<void> {
  return updateDocument(getProfilesPath(uid), profileId, data);
}
